/**
 * Live-update event mapping ("live update" #251).
 *
 * Maps a backend event type from `GET /api/events` onto the `omnis:*` window
 * event(s) the panels ALREADY listen to. That reuse is the whole design: a
 * mutation made on Telegram ends up firing the exact same window event
 * the in-stream SSE parser fires for a web-originated mutation, so no panel had
 * to learn a new refresh mechanism.
 *
 * DELIBERATE SCOPE — this is a *panel refresh* bridge, not a rerun of the SSE
 * parser. `documentService.js` remains the sole authority for the acting tab: it
 * also attaches confirmation cards to message metadata, which this must never do
 * (a card belongs to the conversation turn that produced it, and pending
 * `*_request` types are not published to the feed at all).
 *
 * The pairs below intentionally mirror `documentService.js`'s dispatch chain —
 * including its quirks, which are load-bearing:
 *   - `expense_deleted` also fires `omnis:expense-updated`, because ExpensesPanel
 *     and CalendarPanel only listen to created/updated.
 *   - contact events are consumed only via the generic `omnis:contact-changed`.
 *   - board/ticket types map to their kebab-case name (BoardsPanel's listeners).
 */

// A document created/edited elsewhere should surface in an open File Manager.
// It listens to `omnis:document-upload-complete` for "a new file exists", which
// is exactly the signal here.
const DOC_ARRIVED = ['omnis:document-upload-complete'];

export const LIVE_EVENT_MAP = {
    // Notes
    note_created: ['omnis:note-created'],
    note_updated: ['omnis:note-updated'],
    note_deleted: ['omnis:note-deleted'],

    // Task List (#437) — "Real-time Parity": a task added / ticked / deleted from
    // Telegram (or another tab) reaches the open Task List column.
    task_created: ['omnis:task-created'],
    task_updated: ['omnis:task-updated'],
    task_deleted: ['omnis:task-deleted'],

    // Reminders
    reminder_created: ['omnis:reminder-created'],
    reminder_updated: ['omnis:reminder-updated'],
    reminder_deleted: ['omnis:reminder-deleted'],
    // JIT Bell (#291): the push engine publishes this from the SAME claim that
    // sends the Telegram nudge, so T=0 lights both channels at once.
    // `omnis:reminder-updated` is deliberately reused rather than invented — the
    // Bell dropdown AND the Notes & Reminders panel already refetch on it, which
    // is exactly the pair that must react (the alert appears; the row moves from
    // the active list into Past Reminders, #284).
    reminder_due: ['omnis:reminder-updated'],

    // Finance — `-deleted` has no listener of its own; `-updated` is what the
    // Finance + Calendar panels actually refetch on (mirrors documentService).
    expense_created: ['omnis:expense-created'],
    expense_updated: ['omnis:expense-updated'],
    expense_deleted: ['omnis:expense-deleted', 'omnis:expense-updated'],
    book_deleted: ['omnis:expense-updated'],

    // Calendar
    calendar_event_created: ['omnis:calendar-event-created'],
    calendar_event_updated: ['omnis:calendar-event-updated'],
    calendar_event_deleted: ['omnis:calendar-event-updated'],
    // #533 — a package of >5 events created its pending rows on a phone.
    calendar_event_package: ['omnis:calendar-event-updated'],

    // Boards / tickets
    board_updated: ['omnis:board-updated'],
    board_deleted: ['omnis:board-deleted'],
    ticket_updated: ['omnis:ticket-updated'],
    ticket_deleted: ['omnis:ticket-deleted'],
    ticket_attachment_added: ['omnis:ticket-attachment-added'],
    ticket_attachment_removed: ['omnis:ticket-attachment-removed'],
    comment_deleted: ['omnis:ticket-updated'],

    // Documents
    document_create_complete: DOC_ARRIVED,
    document_edit_complete: DOC_ARRIVED,
    document_deleted: ['omnis:document-deleted'],
    document_moved: ['omnis:document-moved'],

    // Contacts / teams — only the generic events have listeners.
    contact_created: ['omnis:contact-changed'],
    contact_updated: ['omnis:contact-changed'],
    contact_deleted: ['omnis:contact-changed'],
    contact_note_added: ['omnis:contact-changed'],
    team_deleted: ['omnis:team-changed'],

    // Chat (#457 / #448 re-open): the SERVER saved a web assistant message. A
    // tab that reloaded mid-turn (and so lost the stream) refetches the
    // conversation and the reply appears. MainOfficeView owns the listener.
    assistant_message_saved: ['omnis:assistant-message-saved'],
    // #448 re-open: the auto-title is generated after the first message's POST
    // returns; useConversations patches the sidebar row from the detail.
    conversation_title_updated: ['omnis:conversation-title-updated'],

    // Rulebook in Telegram (#503): a rule added/changed/removed on a phone (a
    // Telegram Confirm tap, or a typed yes) — the open Rulebook panel
    // refetches (no rule text rides the feed, so it always loads the server copy).
    rulebook_updated: ['omnis:rulebook-updated'],

    // #545: a corrected card replaced the request's earlier open card(s) on a
    // phone — the open Main Office closes them (MainOfficeView owns the listener;
    // the detail carries `action_ids` + `cards`).
    cards_superseded: ['omnis:cards-superseded'],
};

/**
 * Undo (#210) → the module that must revert.
 *
 * `omnis:action-revoked` exists but has NO listener anywhere, so on its own a
 * revoked action would leave the web UI showing the row it just removed —
 * exactly the ticket's "Undo/Revoke Integration" requirement. The backend sends
 * the action's `category`, so we can additionally fire that module's ordinary
 * refresh event and the panel reverts through its normal path.
 */
export const REVOKE_CATEGORY_EVENTS = {
    expense: ['omnis:expense-deleted', 'omnis:expense-updated'],
    calendar: ['omnis:calendar-event-updated'],
    reminder: ['omnis:reminder-deleted'],
    note: ['omnis:note-deleted'],
    ticket: ['omnis:ticket-deleted', 'omnis:ticket-updated'],
    board: ['omnis:board-deleted'],
    contact: ['omnis:contact-changed'],
    document: ['omnis:document-deleted'],
};

/**
 * Window event names to dispatch for one feed event.
 * @param {{type: string, payload?: object}} event
 * @returns {string[]} possibly empty (an unknown type is ignored, not guessed)
 */
export function windowEventsFor(event) {
    const type = event?.type;
    if (!type) return [];

    if (type === 'action_revoked') {
        const category = event?.payload?.category;
        // Always emit the generic one (a future listener gets it for free), plus
        // the affected module's refresh so the UI actually reverts.
        return ['omnis:action-revoked', ...(REVOKE_CATEGORY_EVENTS[category] || [])];
    }

    return LIVE_EVENT_MAP[type] || [];
}

/**
 * Re-emit one backend event as its window event(s).
 *
 * `detail` carries the backend payload plus a `liveUpdate: true` marker, so a
 * handler can tell a cross-channel refresh from an in-stream one if it ever
 * needs to. Existing handlers ignore `detail` entirely and just refetch.
 */
export function dispatchLiveEvent(event) {
    const names = windowEventsFor(event);
    for (const name of names) {
        try {
            window.dispatchEvent(new CustomEvent(name, {
                detail: { ...(event.payload || {}), type: event.type, liveUpdate: true },
            }));
        } catch (_) { /* a listener throwing must not stop the rest */ }
    }
    return names.length;
}
